/**
 * RunnerRowV2 Component
 * Desktop: Table row matching Pencil `Cnv0o`
 * Mobile: Card matching Pencil `EGQHx`
 *
 * Desktop columns: RANK | RUNNER (avatar+name+bib) | PACE | LAST CP | NEXT CP | STATUS | FOLLOW
 * Mobile card: rank + avatar + name/bib/pace + last station + status + follow
 */

import type { RunnerSummary } from '../../lib/types';
import { formatTimeInZone } from '../../lib/timeFormatter';
import { RunnerAvatarV2 } from './RunnerAvatarV2';
import { StatusBadgeV2 } from './StatusBadgeV2';
import { FollowStarV2 } from './FollowStarV2';

interface RunnerRowV2Props {
  runner: RunnerSummary;
  onClick: (runnerId: string) => void;
  raceTimeZone: string;
  isFollowed: boolean;
  onToggleFollow: (bib: string) => void;
  /** Next aid station name (inferred from station list) */
  nextStation?: string;
  /** Map of station name → mileage, used to compute average pace to last checkpoint */
  stationMileage?: Map<string, number>;
  /** Total race distance in miles (mileage of finish station), used for finished runners' pace */
  totalDistanceMiles?: number;
}

/**
 * Average pace to the runner's last known position, formatted as "M:SS/mi".
 * - Finished: total distance / (finish_time - race_start_time)
 * - On course: last station mileage / (last_checkin_time - race_start_time)
 * Returns null when we don't have enough data (no start time, no checkpoint, no distance).
 */
function calculateAveragePace(
  runner: RunnerSummary,
  stationMileage: Map<string, number> | undefined,
  totalDistanceMiles: number | undefined
): string | null {
  if (!runner.race_start_time) return null;
  const startMs = new Date(runner.race_start_time).getTime();
  if (!Number.isFinite(startMs)) return null;

  let endMs: number | null = null;
  let miles: number | null = null;

  if (runner.status === 'finished' && runner.finish_time && totalDistanceMiles && totalDistanceMiles > 0) {
    endMs = new Date(runner.finish_time).getTime();
    miles = totalDistanceMiles;
  } else if (runner.last_checkin_time && runner.last_checkin_station && stationMileage) {
    const m = stationMileage.get(runner.last_checkin_station);
    if (m && m > 0) {
      endMs = new Date(runner.last_checkin_time).getTime();
      miles = m;
    }
  }

  if (endMs == null || miles == null || !Number.isFinite(endMs)) return null;
  const elapsedMs = endMs - startMs;
  if (elapsedMs <= 0) return null;

  const paceMinutes = elapsedMs / 1000 / 60 / miles;
  const baseMins = Math.floor(paceMinutes);
  const roundedSecs = Math.round((paceMinutes - baseMins) * 60);
  const mins = roundedSecs === 60 ? baseMins + 1 : baseMins;
  const secs = roundedSecs === 60 ? 0 : roundedSecs;
  return `${mins}:${String(secs).padStart(2, '0')}/mi`;
}

/** Desktop table row */
export function RunnerRowDesktop({
  runner,
  onClick,
  raceTimeZone,
  isFollowed,
  onToggleFollow,
  nextStation,
  stationMileage,
  totalDistanceMiles,
}: RunnerRowV2Props) {
  const isAnonymous = runner.is_anonymous;
  const pace = calculateAveragePace(runner, stationMileage, totalDistanceMiles);

  return (
    <tr
      onClick={() => !isAnonymous && onClick(runner.id)}
      style={{ cursor: isAnonymous ? 'default' : 'pointer', opacity: isAnonymous ? 0.6 : 1 }}
    >
      {/* RANK */}
      <td className="v2-rank-cell">
        {runner.overall_place ? (
          <>
            <span>{runner.overall_place}</span>
            <span className="v2-rank-label">OVERALL</span>
          </>
        ) : (
          <span style={{ color: 'var(--v2-text-muted)' }}>—</span>
        )}
      </td>

      {/* RUNNER */}
      <td>
        <div className="v2-runner-cell">
          <RunnerAvatarV2
            firstName={isAnonymous ? '?' : runner.first_name}
            lastName={isAnonymous ? '?' : runner.last_name}
          />
          <div className="v2-runner-cell-info">
            <span className="v2-runner-cell-name">
              {isAnonymous
                ? 'Anonymous Runner'
                : `${runner.first_name} ${runner.last_name.toUpperCase()}`}
            </span>
            <span className="v2-runner-cell-bib">
              BIB {runner.bib}
              {runner.gender && ` · ${runner.gender}`}
            </span>
          </div>
        </div>
      </td>

      {/* PACE - average pace to last known position */}
      <td>
        <div className="v2-checkpoint-cell">
          {pace ?? <span style={{ color: 'var(--v2-text-muted)' }}>—</span>}
        </div>
      </td>

      {/* LAST CHECKPOINT */}
      <td>
        <div className="v2-checkpoint-cell">
          {runner.last_checkin_station ? (
            <>
              <span>{runner.last_checkin_station}</span>
              {runner.last_checkin_time && (
                <>
                  {'  '}
                  <span style={{ color: 'var(--v2-text-muted)' }}>
                    {formatTimeInZone(runner.last_checkin_time, raceTimeZone)}
                  </span>
                </>
              )}
            </>
          ) : (
            <span style={{ color: 'var(--v2-text-muted)' }}>—</span>
          )}
        </div>
      </td>

      {/* NEXT CHECKPOINT */}
      <td>
        <div className="v2-checkpoint-cell">
          {nextStation || <span style={{ color: 'var(--v2-text-muted)' }}>—</span>}
        </div>
      </td>

      {/* STATUS */}
      <td>
        <StatusBadgeV2 status={runner.status} label={runner.status_short_label} />
      </td>

      {/* FOLLOW */}
      <td>
        {!isAnonymous && (
          <FollowStarV2
            isFollowed={isFollowed}
            onToggle={() => onToggleFollow(runner.bib)}
          />
        )}
      </td>
    </tr>
  );
}

/** Mobile card row */
export function RunnerRowMobile({
  runner,
  onClick,
  raceTimeZone,
  isFollowed,
  onToggleFollow,
}: RunnerRowV2Props) {
  const isAnonymous = runner.is_anonymous;

  return (
    <div
      className="v2-mobile-card"
      onClick={() => !isAnonymous && onClick(runner.id)}
      style={{ opacity: isAnonymous ? 0.6 : 1 }}
    >
      {/* Rank */}
      <div className="v2-mobile-card-rank">
        <span className="v2-mobile-card-rank-num">
          {runner.overall_place || '—'}
        </span>
        <span className="v2-mobile-card-rank-label">OVR</span>
      </div>

      {/* Avatar */}
      <RunnerAvatarV2
        firstName={isAnonymous ? '?' : runner.first_name}
        lastName={isAnonymous ? '?' : runner.last_name}
        size="sm"
      />

      {/* Info */}
      <div className="v2-mobile-card-info">
        <span className="v2-mobile-card-name">
          {isAnonymous
            ? 'Anonymous Runner'
            : `${runner.first_name} ${runner.last_name.toUpperCase()}`}
        </span>
        <div className="v2-mobile-card-bib-pace">
          <span>BIB {runner.bib}</span>
          {runner.gender && <span>{runner.gender}</span>}
        </div>
        <span className="v2-mobile-card-last">
          {runner.last_checkin_station
            ? `Last: ${runner.last_checkin_station}`
            : 'Not checked in'}
          {runner.last_checkin_time &&
            ` ${formatTimeInZone(runner.last_checkin_time, raceTimeZone)}`}
        </span>
      </div>

      {/* Right: Status + Follow */}
      <div className="v2-mobile-card-right">
        <StatusBadgeV2 status={runner.status} label={runner.status_short_label} />
        {!isAnonymous && (
          <FollowStarV2
            isFollowed={isFollowed}
            onToggle={() => onToggleFollow(runner.bib)}
            size={14}
          />
        )}
      </div>
    </div>
  );
}
